import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Circus Daily tabs
 * - segmented: filled pill track (lobby nav / gameplay) — Figma 10136:15995
 * - primary: underline group (leaderboard Daily/Monthly) — Figma 10017:7248
 * - pill: outline chips (leaderboard day/week selectors) — Figma 10017:7186
 */
const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva('flex items-center', {
  variants: {
    variant: {
      segmented: 'w-full rounded-xl bg-surface-muted p-0.5',
      /** Figma Primary Group — shared bottom rule */
      primary: 'w-full border-b-2 border-border',
      /** Figma Secondary Group — horizontal chip row */
      pill: 'w-full gap-4 overflow-x-auto bg-transparent p-0',
    },
  },
  defaultVariants: {
    variant: 'segmented',
  },
});

const tabsTriggerVariants = cva(
  'inline-flex items-center justify-center font-sans transition-colors ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        segmented: [
          'min-w-0 flex-1 rounded-[10px] px-2 py-3 text-center text-xs font-semibold leading-3',
          'text-muted-foreground hover:text-white',
          'data-[state=active]:bg-white data-[state=active]:text-background',
        ].join(' '),
        primary: [
          'min-w-0 flex-1 -mb-0.5 border-b-2 border-transparent pb-4 text-center text-sm font-normal leading-4',
          'text-white/80 hover:text-white',
          'data-[state=active]:border-primary data-[state=active]:text-white',
        ].join(' '),
        pill: [
          'h-8 shrink-0 gap-2 rounded-full border-2 border-border px-4 text-xs font-normal leading-3',
          'text-white/80 hover:text-white',
          'data-[state=active]:border-primary data-[state=active]:text-white',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'segmented',
    },
  }
);

type TabsVariant = NonNullable<VariantProps<typeof tabsListVariants>['variant']>;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>
>(({ className, variant = 'segmented', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ variant }), className)}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof tabsTriggerVariants>
>(({ className, variant = 'segmented', children, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  >
    <span className="truncate">{children}</span>
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export type SegmentedTabItem = {
  value: string;
  label: React.ReactNode;
  'data-test-id'?: string;
};

export interface SegmentedTabsProps {
  items: SegmentedTabItem[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  listClassName?: string;
  'aria-label'?: string;
  /** segmented (default) | primary underline | pill outline chips */
  variant?: TabsVariant;
}

/** Controlled tab control — lobby nav, gameplay, and leaderboard periods. */
function SegmentedTabs({
  items,
  value,
  onValueChange,
  className,
  listClassName,
  'aria-label': ariaLabel,
  variant = 'segmented',
}: SegmentedTabsProps) {
  return (
    <Tabs value={value} onValueChange={onValueChange} className={className}>
      <TabsList variant={variant} className={listClassName} aria-label={ariaLabel}>
        {items.map(item => (
          <TabsTrigger
            key={item.value}
            value={item.value}
            variant={variant}
            data-test-id={item['data-test-id']}
          >
            {item.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  SegmentedTabs,
  tabsListVariants,
  tabsTriggerVariants,
};
